/**
 * Energy-cert wall wizard — pure functions (ZERO React/store).
 *
 * Two responsibilities, both pure:
 *   1. `recomputeWalls` — the shape-driven prune/extend that the legacy
 *      `BuildingLayout.context` ran as a mount `[globalShapeType]` useEffect.
 *      MODULE-CONTRACT §3 / spec §5: run it as a PURE handler inside
 *      `setGlobalShapeType` (`onChange → recomputeWalls → patch`), NOT a mount
 *      effect — that makes the F-B1 hydration cascade STRUCTURALLY impossible
 *      (no mount effect to fight the store hydrate). The count-only churn guard
 *      (T↔U both 8 walls → preserve, don't reprune) lives INSIDE here.
 *   2. `calculateWallLengths` — lifted verbatim from
 *      `utils/purchaseFlow/buildingLayoutWallsCalculation.ts` (pure util). The
 *      shape-driven auto-compute (c=a, d=b, e=c-a, …) that fills the derived
 *      wall lengths + disables their inputs.
 */

import { GlobalShapeType, Wall } from 'constants/purchaseFlow'

/** View-model shape for a single wall (mirrors the legacy `WallType` entry). */
export interface EnergyWallView {
  mounted: boolean
  insulated: boolean
  length: string
  insulation: string
}

/** Positional A–H wall map (only the shape's active walls are present). */
export type EnergyWalls = Partial<Record<Wall, EnergyWallView>>

/** An empty (fresh) wall entry. */
export const emptyWall = (): EnergyWallView => ({ mounted: false, insulated: false, length: '', insulation: '' })

/**
 * The set of active walls per global shape (legacy prune, expressed as a
 * keep-list): RECTANGLE→A-D, L_SHAPE→A-F, T/U_SHAPE→A-H, OTHER→∅.
 */
export const wallsForShape: Record<GlobalShapeType, Wall[]> = {
  [GlobalShapeType.RECTANGLE]: [Wall.A, Wall.B, Wall.C, Wall.D],
  [GlobalShapeType.L_SHAPE]: [Wall.A, Wall.B, Wall.C, Wall.D, Wall.E, Wall.F],
  [GlobalShapeType.T_SHAPE]: [Wall.A, Wall.B, Wall.C, Wall.D, Wall.E, Wall.F, Wall.G, Wall.H],
  [GlobalShapeType.U_SHAPE]: [Wall.A, Wall.B, Wall.C, Wall.D, Wall.E, Wall.F, Wall.G, Wall.H],
  [GlobalShapeType.OTHER]: [],
}

/**
 * Pure prune/extend of the wall set for a target shape, preserving existing
 * values positionally.
 *
 * Count-only churn guard (legacy parity + spec §5): when the target wall count
 * equals the current count, the shape swap is a no-op on the wall set (T↔U both
 * carry 8 walls → keep the user's values, do NOT reprune). Otherwise prune
 * (shrink) or extend (grow), preserving the values of the walls that survive —
 * strictly better than the legacy reset-to-empty on every count change.
 */
export const recomputeWalls = (shape: GlobalShapeType, prevWalls: EnergyWalls): EnergyWalls => {
  const targetKeys = wallsForShape[shape]

  // Count-only churn guard: equal count ⇒ preserve the existing walls verbatim.
  if (targetKeys.length === Object.keys(prevWalls).length) return prevWalls

  const next: EnergyWalls = {}
  targetKeys.forEach((key) => {
    next[key] = prevWalls[key] ?? emptyWall()
  })
  return next
}

// ─── calculateWallLengths — lifted from utils/purchaseFlow (pure) ─────────────

/**
 * Which walls are auto-computed (and therefore length-disabled) per shape.
 * `target` = the derived wall; `source`/`subtract` = the input walls it derives
 * from. RECTANGLE: c=a, d=b; L_SHAPE: e=c-a, f=b-d; T_SHAPE: f=d, g=c, h=b;
 * U_SHAPE: g=a, h=d-f-b.
 */
export const shapeWallSettings: Record<GlobalShapeType, { target: string; source: string; subtract?: string | string[] }[]> = {
  [GlobalShapeType.RECTANGLE]: [
    { target: 'C', source: 'A' },
    { target: 'D', source: 'B' },
  ],
  [GlobalShapeType.L_SHAPE]: [
    { target: 'E', source: 'C', subtract: 'A' },
    { target: 'F', source: 'B', subtract: 'D' },
  ],
  [GlobalShapeType.T_SHAPE]: [
    { target: 'F', source: 'D' },
    { target: 'G', source: 'C' },
    { target: 'H', source: 'B' },
  ],
  [GlobalShapeType.U_SHAPE]: [
    { target: 'G', source: 'A' },
    { target: 'H', source: 'D', subtract: ['F', 'B'] },
  ],
  [GlobalShapeType.OTHER]: [],
}

/**
 * Recompute the derived wall lengths from the user-entered source walls. Pure —
 * lifted verbatim from the legacy `calculateWallLengths` (comma/decimal handling
 * intact). Returns a new wall map with the `target` lengths filled.
 */
export const calculateWallLengths = (globalShapeType: GlobalShapeType, walls: EnergyWalls): EnergyWalls => {
  const updatedWalls: EnergyWalls = { ...walls }
  const settings = shapeWallSettings[globalShapeType] || []

  const getFloatLength = (key: string) => parseFloat((walls as Record<string, EnergyWallView>)[key]?.length?.replace(/,/g, '.') || '')
  const hasCommaSeparator = (key: string) => (walls as Record<string, EnergyWallView>)[key]?.length?.includes(',')

  settings.forEach(({ target, source, subtract }) => {
    const sourceLength = getFloatLength(source)
    const subtractLength = Array.isArray(subtract)
      ? subtract.reduce((acc, key) => acc + getFloatLength(key), 0)
      : subtract
        ? getFloatLength(subtract)
        : 0

    const resultLength = !isNaN(sourceLength - subtractLength) ? (sourceLength - subtractLength).toString() : ''
    const prev = (walls as Record<string, EnergyWallView>)[target]

    ;(updatedWalls as Record<string, EnergyWallView>)[target] = {
      ...prev,
      length: hasCommaSeparator(source) ? resultLength.replace(/\./g, ',') : resultLength,
      mounted: prev?.mounted || false,
      insulated: prev?.insulated || false,
      insulation: prev?.insulation || '',
    }
  })

  return updatedWalls
}
